import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { classNames } from '@/helpers/classNames';
import { normaliseBetweenTwoRanges } from '@/helpers/normaliseBetweenTwoRanges';

import styles from './WordCloudChart.module.scss';

const LEGEND_COLORS: [{ bg: string; text: string }, { bg: string; text: string }] = [
	{ bg: '#b3dfff', text: '#0040ff' }, // blue-200 / blue-700
	{ bg: '#e1ff95', text: '#548605' }, // green-200 / green-700
];

export type WordCloudChartDataItem = { label: string; value: number };

export type WordCloudChartProps = {
	className?: string;
	sourceLists: [WordCloudChartDataItem[], WordCloudChartDataItem[]];
	reduced?: boolean;
	labels?: [string, string]; // optional legend: [label0, label1]
	colorOverrides?: Record<string, { bg: string; text: string }>;
	classOverrides?: Record<string, string>; // CSS color-scheme class per label (takes priority over colorOverrides)
	hideStatus?: boolean;
};

export const WordCloudChart: React.FC<WordCloudChartProps> = ({
	className,
	sourceLists,
	reduced = true,
	labels,
	colorOverrides,
	classOverrides,
	hideStatus = false,
}) => {
	const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);
	const [hoveredLegend, setHoveredLegend] = useState<number | null>(null);
	const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
	const [dimAlpha, setDimAlpha] = useState(1);
	const animFrameRef = useRef<number | null>(null);
	const currentAlphaRef = useRef(1);
	const rootRef = useRef<HTMLDivElement>(null);

	const getRelativePos = useCallback((e: React.MouseEvent) => {
		return { x: e.clientX, y: e.clientY };
	}, []);

	const animateTo = useCallback((target: number, onDone?: () => void) => {
		if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
		const tick = () => {
			const diff = target - currentAlphaRef.current;
			if (Math.abs(diff) < 0.015) {
				currentAlphaRef.current = target;
				setDimAlpha(target);
				onDone?.();
				return;
			}
			currentAlphaRef.current += diff * 0.15;
			setDimAlpha(currentAlphaRef.current);
			animFrameRef.current = requestAnimationFrame(tick);
		};
		animFrameRef.current = requestAnimationFrame(tick);
	}, []);

	useEffect(() => () => {
		if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
	}, []);
	// Build a shared map: label -> isShared
	const sharedMap = useMemo(() => {
		const map = new Map<string, boolean>();
		for (const item of sourceLists[0]) {
			map.set(item.label, false);
		}
		for (const item of sourceLists[1]) {
			if (map.has(item.label)) {
				map.set(item.label, true);
			} else {
				map.set(item.label, false);
			}
		}
		return map;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sourceLists[0].map((i) => i.label).join(','), sourceLists[1].map((i) => i.label).join(',')]);

	// Merge both lists into a deduplicated array with max value for shared
	const mergedItems = useMemo(() => {
		const valueMap = new Map<string, number>();
		for (const item of sourceLists[0]) {
			valueMap.set(item.label, item.value);
		}
		for (const item of sourceLists[1]) {
			const existing = valueMap.get(item.label);
			if (existing !== undefined) {
				valueMap.set(item.label, Math.max(existing, item.value));
			} else {
				valueMap.set(item.label, item.value);
			}
		}
		return Array.from(valueMap.entries()).map(([label, value]) => ({ label, value }));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sourceLists[0].map((i) => i.label + i.value).join(','), sourceLists[1].map((i) => i.label + i.value).join(',')]);

	const sorted = useMemo(() => {
		return pyramidSort(mergedItems, reduced);
	}, [mergedItems, reduced]);

	const rawValueMap = useMemo(() => {
		const map = new Map<string, number>();
		for (const { label, value } of mergedItems) {
			map.set(label, value);
		}
		return map;
	}, [mergedItems]);

	const rawTotal = useMemo(
		() => mergedItems.reduce((s, i) => s + i.value, 0),
		[mergedItems],
	);

	// [uniqueTotal, sharedTotal] — for legend hover tooltip
	const categoryTotals = useMemo<[number, number]>(() => {
		let unique = 0;
		let shared = 0;
		for (const { label, value } of mergedItems) {
			if (sharedMap.get(label)) shared += value;
			else unique += value;
		}
		return [unique, shared];
	}, [mergedItems, sharedMap]);

	return (
		<div ref={rootRef} className={classNames([styles['root'], className])}>
			<div className={styles['cluster']}>
				{sorted.map((item, index) => (
					<div
						key={index}
						className={classNames([
							styles['word-item'],
							styles[getSizeClass(item.value)],
							styles['solid'],
							classOverrides?.[item.label]
								?? (!colorOverrides?.[item.label] && (
									sharedMap.get(item.label)
										? styles['color-scheme-green']
										: styles['color-scheme-blue']
								)),
						])}
						style={{
							...(classOverrides?.[item.label]
								? { '--bg-color': 'var(--px-color-scheme-200)', '--color': 'var(--px-color-scheme-contrast)' } as React.CSSProperties
								: colorOverrides?.[item.label]
									? { '--bg-color': colorOverrides[item.label].bg, '--color': colorOverrides[item.label].text } as React.CSSProperties
									: undefined),
							opacity: (() => {
									if (hoveredLegend !== null) {
										const isShared = sharedMap.get(item.label) ?? false;
										return (hoveredLegend === 1 ? isShared : !isShared) ? 1 : dimAlpha;
									}
									return hoveredLabel && hoveredLabel !== item.label ? dimAlpha : 1;
								})(),
						}}
						onMouseEnter={(e) => { setHoveredLegend(null); setHoveredLabel(item.label); setMousePos(getRelativePos(e)); animateTo(0.15); }}
						onMouseMove={(e) => setMousePos(getRelativePos(e))}
						onMouseLeave={() => { setMousePos(null); animateTo(1, () => setHoveredLabel(null)); }}
					>
						{toTitleCase(item.label)}
					</div>
				))}
			</div>
			{mousePos && (hoveredLabel || hoveredLegend !== null) && createPortal(
				<div
					className={styles['tooltip']}
					style={{ top: mousePos.y - 8, left: mousePos.x, transform: 'translate(-50%, -100%)' }}
				>
					{hoveredLegend !== null && labels ? (
						<div className={styles['tooltip-value']}>
							{labels[hoveredLegend]} · {rawTotal > 0 ? (categoryTotals[hoveredLegend] / rawTotal * 100).toFixed(1) : '0.0'}%
						</div>
					) : hoveredLabel ? (
						<>
							<div className={styles['tooltip-value']}>
								{toTitleCase(hoveredLabel)} · {rawTotal > 0 ? ((rawValueMap.get(hoveredLabel) ?? 0) / rawTotal * 100).toFixed(1) : '0.0'}%
							</div>
							{!hideStatus && <div className={styles['tooltip-status']}>{sharedMap.get(hoveredLabel) ? 'Shared' : 'Unique'}</div>}
						</>
					) : null}
				</div>,
				document.body,
			)}
			{labels && (
				<div className={styles['legend']}>
					{labels.map((label, i) => (
						<div
							key={label}
							className={styles['legend-item']}
							style={{ cursor: 'default', opacity: hoveredLegend !== null && hoveredLegend !== i ? 0.4 : 1 }}
							onMouseEnter={(e) => { setHoveredLabel(null); setHoveredLegend(i); setMousePos(getRelativePos(e)); animateTo(0.15); }}
							onMouseMove={(e) => setMousePos(getRelativePos(e))}
							onMouseLeave={() => { setMousePos(null); animateTo(1, () => setHoveredLegend(null)); }}
						>
							<div className={styles['legend-circle']} style={{ background: LEGEND_COLORS[i].bg }} />
							<span className={styles['legend-label']}>{label}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

function toTitleCase(s: string): string {
	return s.replace(/\b(?<!')\w/g, c => c.toUpperCase());
}

function getSizeClass(value: number): string {
	switch (true) {
		case value >= 0.8:
			return 'size-l';
		case value >= 0.6:
			return 'size-m';
		case value >= 0.4:
			return 'size-s';
		case value >= 0.2:
			return 'size-xs';
		default:
			return 'size-xxs';
	}
}

function pyramidSort(arr: WordCloudChartDataItem[], reduced: boolean) {
	if (arr.length === 0) {
		return [];
	}
	const newArr: WordCloudChartDataItem[] = [];

	// sort numerically
	arr.sort(function (a, b) {
		return a.value - b.value;
	});
	const topArr = reduced ? arr.slice(-7) : [...arr];
	const min = topArr[0].value;
	const max = topArr[topArr.length - 1].value;
	const inputArr = topArr.map((item) => ({
		...item,
		value: min === max ? 1 : normaliseBetweenTwoRanges(item.value, min, max, 0, 1),
	}));

	// put the biggest in new array
	newArr.push(inputArr.pop() as WordCloudChartDataItem);

	// keep grabbing the biggest remaining item and alternate
	// between pushing and unshifting onto the new array
	while (inputArr.length) {
		newArr[inputArr.length % 2 === 0 ? 'push' : 'unshift'](
			inputArr.pop() as WordCloudChartDataItem,
		);
	}

	return newArr;
}
